import { useEffect, useRef, useState } from 'react'
import Navbar from '@/components/feature/Navbar'
import HeroSection from './components/HeroSection'
import EcosystemSection from './components/EcosystemSection'
import DemandSection from './components/DemandSection'
import CourseSection from './components/CourseSection'
import HomeFooterSection from './components/HomeFooterSection'
import { IntroLoader } from '@/components/common'
import { useAssetRenewStore } from '@/stores'
import heroVideo from '@/assets/home/index-bg.mp4'
import './home.css'

// 记录整页首次加载（例如刷新或直接打开地址）时的入口路径是否为首页
const isInitialPageLoadOnHome = typeof window !== 'undefined' && (window.location.pathname === '/' || window.location.pathname === '/home')

// 标记 IntroLoader 是否已在当前 SPA 会话中播放过
let hasShownHomeIntro = false

export default function Home() {

  const [showIntro, setShowIntro] = useState(() => {
    // 只有当整页首次刷新/加载的路径是首页，且本会话尚未展示过 IntroLoader 时，才加载开场动画
    if (isInitialPageLoadOnHome && !hasShownHomeIntro) {
      hasShownHomeIntro = true
      return true
    }
    return false
  })
  const pageRef = useRef<HTMLDivElement>(null)

  // 离开首页/切换页面时，自动重置清理首页临时资源缓存
  useEffect(() => {
    return () => {
      useAssetRenewStore.getState().clearCache()
    }
  }, [])


  const currentRef = useRef(0)
  const switchingRef = useRef(false)

  useEffect(() => {
    const page = pageRef.current
    if (!page) return
    const screens = Array.from(page.querySelectorAll<HTMLElement>('.home-screen'))
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    let lastScrollTop = page.scrollTop
    let isScrollingDown = true

    const goToScreen = (index: number) => {
      if (switchingRef.current || index < 0 || index >= screens.length) return
      switchingRef.current = true
      currentRef.current = index
      screens[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      window.setTimeout(() => { switchingRef.current = false }, 900)
    }

    const onWheel = (event: WheelEvent) => {
      // 矮视口退回普通长页面，不劫持滚轮
      if (window.innerHeight < 760) return
      if (Math.abs(event.deltaY) < 8) return
      if (switchingRef.current) {
        event.preventDefault()
        return
      }

      const lastScreen = screens[screens.length - 1]
      if (!lastScreen) return
      const hasEnteredFooter = page.scrollTop > lastScreen.offsetTop + 2
      const isLeavingLastScreen = currentRef.current === screens.length - 1 && event.deltaY > 0

      // 最后一屏之后的页尾是普通文档流，进入后释放滚轮，直到自然滚回最后一屏顶部
      if (hasEnteredFooter || isLeavingLastScreen) return

      event.preventDefault()
      goToScreen(currentRef.current + (event.deltaY > 0 ? 1 : -1))
    }

    const onScroll = () => {
      const nextScrollTop = page.scrollTop
      if (Math.abs(nextScrollTop - lastScrollTop) > 1) {
        isScrollingDown = nextScrollTop > lastScrollTop
        lastScrollTop = nextScrollTop
      }
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const screen = entry.target as HTMLElement
        const index = screens.indexOf(screen)
        const hasReachedScreen = entry.intersectionRatio >= 0.62
        const rootTop = entry.rootBounds?.top ?? page.getBoundingClientRect().top
        const isApproachingFromBelow = entry.boundingClientRect.top >= rootTop

        if (!hasReachedScreen) {
          screen.classList.remove('home-screen--animate-enter')
          if (!prefersReducedMotion && index > 0 && isScrollingDown && entry.isIntersecting && isApproachingFromBelow) {
            screen.classList.add('home-screen--prepare-enter')
          } else {
            screen.classList.remove('home-screen--prepare-enter')
          }
          return
        }

        screen.classList.add('home-screen--visible')
        if (!prefersReducedMotion && index > 0 && isScrollingDown) {
          screen.classList.add('home-screen--animate-enter')
        }
        screen.classList.remove('home-screen--prepare-enter')
        if (index >= 0) currentRef.current = index
      })
    }, { root: page, threshold: [0, 0.05, 0.62] })
    screens.forEach((screen) => observer.observe(screen))
    screens[0]?.classList.add('home-screen--visible')
    page.addEventListener('scroll', onScroll, { passive: true })
    page.addEventListener('wheel', onWheel, { passive: false })

    return () => {
      observer.disconnect()
      page.removeEventListener('scroll', onScroll)
      page.removeEventListener('wheel', onWheel)
    }
  }, [])

  return (
    <div className="min-h-screen flex flex-col bg-white font-body">
      {showIntro && <IntroLoader onComplete={() => setShowIntro(false)} />}
      <Navbar />
      <div ref={pageRef} className="home-page">
        <video
          className="home-page__video"
          autoPlay
          muted
          loop
          playsInline
          disablePictureInPicture
          disableRemotePlayback
          controlsList="nodownload no-picture-in-picture"
          aria-hidden="true"
        >
          <source src={heroVideo} type="video/mp4" />
        </video>
        <div className="home-page__veil" aria-hidden="true" />

        <HeroSection isIntroActive={showIntro} />
        <EcosystemSection />
        <DemandSection />
        <CourseSection />
        <HomeFooterSection />
      </div>
    </div>
  )
}
